import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import { getStudyPlan, getImprovementData, getUneMastery } from '../api/endpoints';
import {
  BookOpen, TrendingUp, TrendingDown, Minus, Target, CheckCircle,
  AlertCircle, BarChart3, Clock, Calendar, Zap, Lock
} from 'lucide-react';

const StudyPlanPage = () => {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [improvement, setImprovement] = useState(null);
  const [uneMastery, setUneMastery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tierBlocked, setTierBlocked] = useState(false);

  useEffect(() => {
    document.title = `Study Plan | ${tenant?.name || 'MedMCQ'}`;
  }, [tenant]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [planRes, improvementRes, uneRes] = await Promise.allSettled([
          getStudyPlan(),
          getImprovementData(),
          getUneMastery(),
        ]);

        // Check if study plan was blocked by tier gating
        if (planRes.status === 'rejected' && planRes.reason?.response?.status === 403) {
          setTierBlocked(true);
        } else if (planRes.status === 'fulfilled') {
          setPlan(planRes.value.data);
        }
        if (improvementRes.status === 'fulfilled') setImprovement(improvementRes.value.data);
        if (uneRes.status === 'fulfilled') setUneMastery(uneRes.value.data);
      } catch (err) {
        setError('Unable to load study plan data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (tierBlocked) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="bg-white/5 border border-white/10 rounded-xl p-8 max-w-md text-center">
            <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">AI Study Plan</h2>
            <p className="text-gray-400 mb-6">
              Upgrade to Professional or above to unlock AI-powered personalised study plans that adapt to your performance and exam dates.
            </p>
            <Link
              to="/subscription"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg transition-colors"
            >
              View Plans
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto py-8 px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-700 rounded w-1/3" />
            <div className="h-32 bg-gray-800 rounded" />
            <div className="h-48 bg-gray-800 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  const engagement = improvement?.engagement || {};
  const examReadiness = improvement?.exam_readiness || {};
  const curves = improvement?.curves || {};
  const comparison = improvement?.comparison || {};

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-6 px-4 space-y-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-8 h-8 text-cyan-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Your Study Plan</h1>
            <p className="text-sm text-gray-400">
              Auto-evolving based on your performance data
            </p>
          </div>
        </div>

        {/* UNE Mastery Progress */}
        {uneMastery && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-cyan-400" />
                Core Content Mastery
              </h2>
              <span className="text-2xl font-bold text-cyan-400">
                {Math.round((uneMastery.overall_mastery_pct || 0) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3 mb-3">
              <div
                className="bg-gradient-to-r from-cyan-500 to-emerald-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.round((uneMastery.overall_mastery_pct || 0) * 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">
                {(uneMastery.mastered_subcategories || []).length} subcategories mastered
              </span>
              <span className="text-gray-400">
                {(uneMastery.unmastered_subcategories || []).length} remaining
              </span>
            </div>
          </div>
        )}

        {/* Weekly Engagement */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-cyan-400" />
            This Week
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetricCard
              label="Sessions"
              value={engagement.sessions_this_week || 0}
              target={3}
              unit="/ 3 min"
            />
            <MetricCard
              label="Questions"
              value={engagement.questions_this_week || 0}
              target={75}
              unit="/ 75 min"
            />
            <MetricCard
              label="Day Streak"
              value={engagement.streak_days || 0}
              unit="days"
            />
            <MetricCard
              label="Consistency"
              value={Math.round((engagement.consistency_score || 0) * 100)}
              unit="%"
            />
          </div>
          <div className="mt-3 flex items-center gap-2">
            {engagement.meets_minimum ? (
              <>
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-400">On track this week</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-amber-400">Below minimum study target</span>
              </>
            )}
          </div>
        </div>

        {/* Current Study Plan */}
        {plan && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-cyan-400" />
              Current Strategy
            </h2>

            {plan.current_phase && (
              <div className="mb-4">
                <span className="text-sm text-gray-400">Learning Phase: </span>
                <span className={`text-sm font-medium px-2 py-1 rounded ${
                  plan.current_phase === 'maintenance' ? 'bg-emerald-500/20 text-emerald-300' :
                  plan.current_phase === 'growth' ? 'bg-cyan-500/20 text-cyan-300' :
                  plan.current_phase === 'remediation' ? 'bg-amber-500/20 text-amber-300' :
                  'bg-gray-500/20 text-gray-300'
                }`}>
                  {(plan.current_phase || 'Discovery').charAt(0).toUpperCase() + (plan.current_phase || 'discovery').slice(1)}
                </span>
              </div>
            )}

            {plan.strategy_summary && (
              <p className="text-gray-300 text-sm mb-4">{plan.strategy_summary}</p>
            )}

            {/* Priority Categories */}
            {plan.categories_to_prioritise && plan.categories_to_prioritise.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-400 mb-2">Priority Categories</h3>
                <div className="space-y-2">
                  {plan.categories_to_prioritise.map((cat, i) => (
                    <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                      <span className="text-sm text-white capitalize">
                        {typeof cat === 'string' ? cat : cat.category}
                      </span>
                      {typeof cat === 'object' && cat.current_accuracy != null && (
                        <span className="text-xs text-gray-400">
                          {Math.round(cat.current_accuracy * 100)}% accuracy
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Weekly Targets */}
            {plan.weekly_question_target && (
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>Target: {plan.weekly_question_target} questions/week</span>
                <span>{plan.minimum_sessions_per_week || 3} sessions/week minimum</span>
              </div>
            )}
          </div>
        )}

        {/* Category Progress Table */}
        {Object.keys(curves).length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-cyan-400" />
              Category Progress
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 border-b border-white/10">
                    <th className="text-left py-2 pr-4">Category</th>
                    <th className="text-center py-2 px-2">Accuracy</th>
                    <th className="text-center py-2 px-2">Projected</th>
                    <th className="text-center py-2 px-2">Questions</th>
                    <th className="text-center py-2 px-2">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(curves)
                    .sort((a, b) => (a[1].current_accuracy || 0) - (b[1].current_accuracy || 0))
                    .map(([category, data]) => (
                    <tr key={category} className="border-b border-white/5">
                      <td className="py-2.5 pr-4 text-white capitalize">{category}</td>
                      <td className="text-center py-2.5 px-2">
                        <span className={`font-medium ${
                          (data.current_accuracy || 0) >= 0.80 ? 'text-emerald-400' :
                          (data.current_accuracy || 0) >= 0.60 ? 'text-cyan-400' :
                          'text-amber-400'
                        }`}>
                          {Math.round((data.current_accuracy || 0) * 100)}%
                        </span>
                      </td>
                      <td className="text-center py-2.5 px-2 text-gray-400">
                        {Math.round((data.projected_accuracy || 0) * 100)}%
                      </td>
                      <td className="text-center py-2.5 px-2 text-gray-400">
                        {data.total_questions || 0}
                      </td>
                      <td className="text-center py-2.5 px-2">
                        <TrendIcon trend={data.trend} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Exam Readiness */}
        {examReadiness.at_current_pace && examReadiness.at_current_pace !== 'insufficient_data' && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-cyan-400" />
              Exam Readiness
            </h2>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium px-3 py-1.5 rounded ${
                examReadiness.at_current_pace === 'on_track' ? 'bg-emerald-500/20 text-emerald-300' :
                examReadiness.at_current_pace === 'slow' ? 'bg-amber-500/20 text-amber-300' :
                'bg-red-500/20 text-red-300'
              }`}>
                {examReadiness.at_current_pace === 'on_track' ? 'On Track' :
                 examReadiness.at_current_pace === 'slow' ? 'Behind Pace' :
                 'At Risk'}
              </span>
              {examReadiness.projected_weeks != null && (
                <span className="text-sm text-gray-400">
                  ~{examReadiness.projected_weeks} weeks to target at current pace
                </span>
              )}
            </div>
            <div className="mt-2 text-sm text-gray-400">
              Current average: {Math.round((examReadiness.current_avg_accuracy || 0) * 100)}%
              {' / '}
              Target: {Math.round((examReadiness.target_accuracy || 0.85) * 100)}%
            </div>
          </div>
        )}

        {/* No Data State */}
        {!plan && Object.keys(curves).length === 0 && !uneMastery && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-white mb-2">No study plan yet</h3>
            <p className="text-gray-400 text-sm">
              Complete your first study session to generate your personalised study plan.
              The plan evolves automatically after each session.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

// ── Helper Components ──

const MetricCard = ({ label, value, target, unit }) => {
  const isOnTarget = target ? value >= target : true;
  return (
    <div className="bg-white/5 rounded-lg p-3 text-center">
      <div className={`text-2xl font-bold ${isOnTarget ? 'text-white' : 'text-amber-400'}`}>
        {value}
      </div>
      <div className="text-xs text-gray-400 mt-1">
        {label} {unit && <span className="text-gray-500">{unit}</span>}
      </div>
    </div>
  );
};

const TrendIcon = ({ trend }) => {
  switch (trend) {
    case 'improving':
      return <TrendingUp className="w-4 h-4 text-emerald-400 mx-auto" />;
    case 'declining':
      return <TrendingDown className="w-4 h-4 text-red-400 mx-auto" />;
    case 'plateau':
      return <Minus className="w-4 h-4 text-amber-400 mx-auto" />;
    default:
      return <Minus className="w-4 h-4 text-gray-500 mx-auto" />;
  }
};

export default StudyPlanPage;
